import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Pressable } from "react-native";
import { View, Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Icon } from "@src/components/Icon";
import {
  startOfWeek,
  startOfDay,
  addDays,
  subWeeks,
  addWeeks,
  subMonths,
  addMonths,
  isSameDay,
  isAfter,
  getDay,
  format,
  isSameYear,
} from "date-fns";
import Animated, { FadeInLeft, FadeInRight, FadeOut } from "react-native-reanimated";

interface WeekCalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  /** "Today" in the camera's timezone — used for forward-navigation boundaries */
  today: Date;
}

const DAY_NAMES = ["M", "Tu", "W", "Th", "F", "Sa", "Su"];

export const WeekCalendar = memo(({ selectedDate, onDateSelect, today }: WeekCalendarProps) => {
  const { theme } = useUnistyles();
  const directionRef = useRef<"forward" | "backward">("backward");

  // Anchor week controls the visible range - only changes via chevron/today navigation
  const [anchorWeek, setAnchorWeek] = useState(() => startOfWeek(selectedDate, { weekStartsOn: 1 }));

  // The 2-week range: previous week + anchor week
  const rangeStart = useMemo(() => subWeeks(anchorWeek, 1), [anchorWeek]);
  const rangeEnd = useMemo(() => addDays(anchorWeek, 6), [anchorWeek]);

  const allDays = useMemo(() => {
    return Array.from({ length: 14 }, (_, i) => addDays(rangeStart, i));
  }, [rangeStart]);

  const dateRangeLabel = useMemo(() => {
    if (isSameYear(rangeStart, rangeEnd)) {
      return `${format(rangeStart, "MMM d")} - ${format(rangeEnd, "MMM d, yyyy")}`;
    } else {
      return `${format(rangeStart, "MMM d, yyyy")} - ${format(rangeEnd, "MMM d, yyyy")}`;
    }
  }, [rangeStart, rangeEnd]);

  const canGoForwardWeek = useMemo(() => {
    const nextAnchor = addWeeks(anchorWeek, 1);
    return !isAfter(startOfDay(nextAnchor), today);
  }, [anchorWeek, today]);

  const canGoForwardMonth = useMemo(() => {
    const nextAnchor = addMonths(anchorWeek, 1);
    return !isAfter(startOfDay(nextAnchor), today);
  }, [anchorWeek, today]);

  const clampToday = useCallback(
    (date: Date): Date => {
      if (isAfter(startOfDay(date), today)) {
        return today;
      }
      return date;
    },
    [today],
  );

  const handlePreviousMonth = useCallback(() => {
    directionRef.current = "backward";
    const newDate = subMonths(selectedDate, 1);
    const newAnchor = startOfWeek(newDate, { weekStartsOn: 1 });
    setAnchorWeek(newAnchor);
    onDateSelect(newDate);
  }, [selectedDate, onDateSelect]);

  const handleNextMonth = useCallback(() => {
    if (canGoForwardMonth) {
      directionRef.current = "forward";
      const newDate = clampToday(addMonths(selectedDate, 1));
      const newAnchor = startOfWeek(newDate, { weekStartsOn: 1 });
      setAnchorWeek(newAnchor);
      onDateSelect(newDate);
    }
  }, [selectedDate, onDateSelect, canGoForwardMonth, clampToday]);

  const handlePreviousWeek = useCallback(() => {
    directionRef.current = "backward";
    const newAnchor = subWeeks(anchorWeek, 1);
    setAnchorWeek(newAnchor);
    onDateSelect(subWeeks(selectedDate, 1));
  }, [anchorWeek, selectedDate, onDateSelect]);

  const handleNextWeek = useCallback(() => {
    if (canGoForwardWeek) {
      directionRef.current = "forward";
      const newAnchor = addWeeks(anchorWeek, 1);
      setAnchorWeek(newAnchor);
      onDateSelect(clampToday(addWeeks(selectedDate, 1)));
    }
  }, [anchorWeek, selectedDate, onDateSelect, canGoForwardWeek, clampToday]);

  const handleSelectToday = useCallback(() => {
    if (!isSameDay(selectedDate, today)) {
      directionRef.current = isAfter(selectedDate, today) ? "backward" : "forward";
      setAnchorWeek(startOfWeek(today, { weekStartsOn: 1 }));
      onDateSelect(today);
    }
  }, [selectedDate, onDateSelect, today]);

  const getDayNameIndex = (date: Date): number => {
    const dayOfWeek = getDay(date);
    return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  };

  const iconSize = theme.spacing["5xl"];
  const iconColor = theme.colors.text;
  const iconColorDisabled = theme.colors.grey[400];

  const chevronOverlap = -iconSize * 0.7;
  const enteringAnimation = directionRef.current === "forward" ? FadeInRight : FadeInLeft;
  const exitingAnimation = FadeOut;

  return (
    <View px="sm" py="lg" rounded={theme.spacing.xl}>
      <View flexDirection="row" alignItems="center" justifyContent="space-between" height={30}>
        <View flexDirection="row" alignItems="center">
          {/* Previous Month (<<) */}
          <Pressable onPress={handlePreviousMonth} hitSlop={theme.spacing.sm} style={styles.previousMonth}>
            <Icon name="chevronLeft" size={iconSize} color={iconColor} />
            <Icon name="chevronLeft" size={iconSize} color={iconColor} style={{ marginLeft: chevronOverlap }} />
          </Pressable>

          {/* Previous Week (<) */}
          <Pressable onPress={handlePreviousWeek} hitSlop={theme.spacing.sm}>
            <Icon name="chevronLeft" size={iconSize} color={iconColor} />
          </Pressable>

          <View mx="md">
            <Text textStyle="xs/semibold">{dateRangeLabel}</Text>
          </View>

          {/* Next Week (>) */}
          <Pressable onPress={handleNextWeek} disabled={!canGoForwardWeek} hitSlop={theme.spacing.sm}>
            <Icon name="chevronRight" size={iconSize} color={canGoForwardWeek ? iconColor : iconColorDisabled} />
          </Pressable>

          {/* Next Month (>>) */}
          <Pressable
            onPress={handleNextMonth}
            disabled={!canGoForwardMonth}
            hitSlop={theme.spacing.sm}
            style={styles.nextMonth}
          >
            <Icon name="chevronRight" size={iconSize} color={canGoForwardMonth ? iconColor : iconColorDisabled} />
            <Icon
              name="chevronRight"
              size={iconSize}
              color={canGoForwardMonth ? iconColor : iconColorDisabled}
              style={{ marginLeft: chevronOverlap }}
            />
          </Pressable>
        </View>
        {!isSameDay(selectedDate, today) && (
          <Pressable onPress={handleSelectToday} hitSlop={theme.spacing.sm}>
            <View
              alignItems="center"
              height={30}
              justifyContent="center"
              px="xl"
              rounded={theme.spacing.xl}
              borderWidth={1}
              style={styles.todayLabel}
            >
              <Text textStyle="xs/regular">Today</Text>
            </View>
          </Pressable>
        )}
      </View>
      {/* Two weeks in a single row */}
      <Animated.View entering={enteringAnimation} exiting={exitingAnimation} key={rangeStart.toISOString()}>
        <View flexDirection="row" mt="md" justifyContent="space-between">
          {allDays.map((day) => {
            const isSelected = isSameDay(day, selectedDate);
            const dayNameIndex = getDayNameIndex(day);
            const isFutureDate = isAfter(startOfDay(day), today);
            const number = day.getDate();

            return (
              <Pressable
                key={day.toISOString()}
                onPress={() => !isFutureDate && onDateSelect(day)}
                disabled={isFutureDate}
                style={[
                  styles.container,
                  {
                    opacity: isFutureDate ? 0.5 : 1,
                  },
                ]}
              >
                <View>
                  <Text
                    textStyle={isSelected ? "xs/bold" : "xs/medium"}
                    color="textMuted"
                    mb="sm"
                    style={{
                      color: isSelected ? theme.colors.interactive.buttons.default_background : theme.colors.text,
                    }}
                  >
                    {DAY_NAMES[dayNameIndex]}
                  </Text>
                </View>

                <View
                  w={32}
                  h={32}
                  rounded={theme.spacing.xs}
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    backgroundColor: isSelected ? theme.colors.interactive.buttons.default_background : "transparent",
                  }}
                >
                  <Text
                    textStyle="md/semibold"
                    style={{ color: isSelected ? theme.colors.white.default : theme.colors.text }}
                  >
                    {number}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      </Animated.View>
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  container: {
    alignItems: "center",
    paddingVertical: theme.spacing.xs,
  },
  previousMonth: {
    flexDirection: "row",
  },
  nextMonth: {
    flexDirection: "row",
  },
  todayLabel: {
    backgroundColor:
      theme.currentTheme === "dark" ? theme.colors.interactive.controls.secondary_background : "transparent",
    borderColor: theme.colors.blue[400],
  },
}));
